import { useState } from "react";
import type { StepDetailProps } from "./index";
import { STEP_OUTPUT_SCHEMAS, zodIssuesToErrors } from "./schemas";
import { StepActions } from "./StepActions";
import { MarkdownText, hasMarkdown } from "../review/MarkdownText";

function readChannel(inputs: Record<string, unknown>): string {
  return typeof inputs["channel"] === "string"
    ? (inputs["channel"] as string)
    : "—";
}

function readRecipients(inputs: Record<string, unknown>): string[] {
  const r = inputs["recipients"];
  if (!Array.isArray(r)) return [];
  return r.filter((s): s is string => typeof s === "string");
}

function readBody(inputs: Record<string, unknown>): string {
  const b = inputs["body_draft"];
  return typeof b === "string" ? b : "";
}

export function CommDetail({
  step,
  play,
  workspaceId,
  currentUserId,
  persona,
}: StepDetailProps) {
  const channel = readChannel(step.inputs);
  const recipients = readRecipients(step.inputs);
  const body = readBody(step.inputs);
  const [sentAt, setSentAt] = useState<string>(() =>
    new Date().toISOString().slice(0, 16),
  );
  const [receiptRef, setReceiptRef] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const buildOutput = (): Record<string, unknown> | null => {
    const iso = sentAt ? new Date(sentAt).toISOString() : "";
    const payload: Record<string, unknown> = { sent_at: iso };
    if (receiptRef) payload["receipt_ref"] = receiptRef;
    const parsed = STEP_OUTPUT_SCHEMAS["comm"].safeParse(payload);
    if (!parsed.success) {
      const map: Record<string, string> = {};
      for (const e of zodIssuesToErrors(parsed.error)) map[e.path] = e.message;
      setErrors(map);
      return null;
    }
    setErrors({});
    return parsed.data as Record<string, unknown>;
  };

  return (
    <div className="ho-detail ho-comm" data-testid="step-detail-comm">
      <div className="ho-eb">
        {(step.type || "COMM").toUpperCase()} ·{" "}
        {step.state === "completed"
          ? "SENT"
          : step.state === "running"
            ? "REVIEW & SEND"
            : "DRAFT"}
      </div>
      <h2 className="ho-h2">{step.title}</h2>
      <div className="ho-meta-row">
        <span className="pill">{channel}</span>
        <span style={{ color: "var(--text-3)" }}>· {recipients.length} recipients</span>
      </div>
      <div className="ho-msg" data-testid="comm-draft">
        <div className="ho-msg-h">
          <span className="ho-msg-to">TO</span>
          <span className="ho-msg-a">
            {recipients.slice(0, 3).join(", ")}
            {recipients.length > 3 ? ` +${recipients.length - 3} more` : ""}
          </span>
        </div>
        <div className="ho-msg-body" style={body && hasMarkdown(body) ? undefined : { whiteSpace: "pre-wrap" }}>
          {body
            ? (hasMarkdown(body) ? <MarkdownText>{body}</MarkdownText> : body)
            : "(no draft body)"}
        </div>
      </div>

      {step.state === "running" ? (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <label>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                letterSpacing: ".14em",
              }}
            >
              SENT AT
            </span>
            <input
              type="datetime-local"
              data-testid="comm-sent-at"
              value={sentAt}
              onChange={(e) => setSentAt(e.target.value)}
              style={{
                marginTop: 4,
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
              }}
            />
          </label>
          {errors["sent_at"] ? (
            <span
              style={{ color: "var(--danger)", fontSize: 12 }}
              data-testid="comm-error-sent-at"
            >
              {errors["sent_at"]}
            </span>
          ) : null}
          <label>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                letterSpacing: ".14em",
              }}
            >
              RECEIPT REF (optional)
            </span>
            <input
              type="text"
              data-testid="comm-receipt-ref"
              value={receiptRef}
              onChange={(e) => setReceiptRef(e.target.value)}
              style={{
                marginTop: 4,
                width: "100%",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
              }}
            />
          </label>
        </div>
      ) : null}

      <StepActions
        step={step}
        workspaceId={workspaceId}
        playId={play.id}
        currentUserId={currentUserId}
        persona={persona}
        buildOutput={buildOutput}
      />
    </div>
  );
}
